import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext } from '@/components/ui/pagination'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { MapPin, GraduationCap, Utensils, Wallet, Code2, Github, Linkedin, Mail } from "lucide-react"
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import apiClient from '@/lib/axios'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Announcement {
  id: number;
  title: string;
  date: string;
  description: string;
  link?: string;
}

interface Bolum {
  _id: string;
  ad: string;
}

interface Istatistikler {
  toplamSinav?: number;
  toplamDers?: number;
  toplamEgitmen?: number;
  spotifySayisi?: number;
}


interface Restoran {
  _id: string;
  kapakFoto: string;
  isim: string;
  konum: string;
  googleMapsLink: string;
}

interface Aktivite {
  _id: string;
  kapakFoto: string;
  isim: string;
  konum: string;
  googleMapsLink: string;
}

interface OtobusSaat {
  _id: string;
  rota: string;
  saatler: Array<{
    pazaryerinden: string;
    hedefe: string;
  }>;
  not?: string;
}

export default function Dashboard() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [aylikMenu, setAylikMenu] = useState<string | null>(null);
  const [bolumler, setBolumler] = useState<Bolum[]>([]);
  const [loading, setLoading] = useState(true);
  const [bolumlerLoading, setBolumlerLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDevelopers, setShowDevelopers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [istatistikler, setIstatistikler] = useState<Istatistikler>({});
  const [istatistiklerLoading, setIstatistiklerLoading] = useState(true);

  // Yeni state'ler
  const [restoranlar, setRestoranlar] = useState<Restoran[]>([]);
  const [aktiviteler, setAktiviteler] = useState<Aktivite[]>([]);
  const [otobusSaatleri, setOtobusSaatleri] = useState<OtobusSaat[]>([]);
  const [restoranlarLoading, setRestoranlarLoading] = useState(false);
  const [aktivitelerLoading, setAktivitelerLoading] = useState(false);
  const [otobusSaatleriLoading, setOtobusSaatleriLoading] = useState(false);

  // Tab state'i
  const [activeTab, setActiveTab] = useState<string>('genel');

  const getCurrentPageAnnouncements = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return announcements.slice(startIndex, endIndex);
  };

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await apiClient.get('/duyurular');
        setAnnouncements(response.data);
      } catch (err) {
        setError('Duyurular yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();

    const fetchAylikMenu = async () => {
      try {
        const response = await apiClient.get('/aylik-menu');
        setAylikMenu(response.data.pdfLink);
      } catch (err) {
        setError('Aylık menü yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchAylikMenu();
  }, []);

  useEffect(() => {
    const fetchBolumler = async () => {
      try {
        const response = await apiClient.get('/bolum');
        setBolumler(response.data);
      } catch (err) {
        // Error handled silently
      } finally {
        setBolumlerLoading(false);
      }
    };

    fetchBolumler();
  }, []);

  useEffect(() => {
    const fetchIstatistikler = async () => {
      try {
        const response = await apiClient.get('/istatistikler');
        setIstatistikler(response.data);
      } catch (err) {
        // Error handled silently
      } finally {
        setIstatistiklerLoading(false);
      }
    };

    fetchIstatistikler();
  }, []);

  // Restoran verilerini çek - sadece sokakLezzetleri tab'ı açıldığında
  useEffect(() => {
    if (activeTab !== 'sokakLezzetleri') return;

    // Eğer zaten veri varsa tekrar çekme
    if (restoranlar.length > 0) return;

    setRestoranlarLoading(true);
    const fetchRestoranlar = async () => {
      try {
        const response = await apiClient.get('/restoran');
        setRestoranlar(response.data);
      } catch (err) {
        console.error('Restoranlar yüklenirken hata:', err);
      } finally {
        setRestoranlarLoading(false);
      }
    };

    fetchRestoranlar();
  }, [activeTab, restoranlar.length]);

  // Aktivite verilerini çek - sadece dogaMangal tab'ı açıldığında
  useEffect(() => {
    if (activeTab !== 'dogaMangal') return;

    // Eğer zaten veri varsa tekrar çekme
    if (aktiviteler.length > 0) return;

    setAktivitelerLoading(true);
    const fetchAktiviteler = async () => {
      try {
        const response = await apiClient.get('/aktivite');
        setAktiviteler(response.data);
      } catch (err) {
        console.error('Aktiviteler yüklenirken hata:', err);
      } finally {
        setAktivitelerLoading(false);
      }
    };

    fetchAktiviteler();
  }, [activeTab, aktiviteler.length]);

  // Otobüs saatlerini çek - sadece ozelOtobus tab'ı açıldığında
  useEffect(() => {
    if (activeTab !== 'ozelOtobus') return;

    // Eğer zaten veri varsa tekrar çekme
    if (otobusSaatleri.length > 0) return;

    setOtobusSaatleriLoading(true);
    const fetchOtobusSaatleri = async () => {
      try {
        const response = await apiClient.get('/otobus-saat');
        setOtobusSaatleri(response.data);
      } catch (err) {
        console.error('Otobüs saatleri yüklenirken hata:', err);
      } finally {
        setOtobusSaatleriLoading(false);
      }
    };

    fetchOtobusSaatleri();
  }, [activeTab, otobusSaatleri.length]);
  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className='mb-4'>
          <h1 className='text-2xl font-bold tracking-tight'>Anasayfa</h1>
        </div>
        <Tabs
          orientation='vertical'
          defaultValue='genel'
          className='space-y-4'
          onValueChange={(value) => setActiveTab(value)}
        >
          <div className='w-full overflow-x-auto pb-2'>
            <TabsList>
              <TabsTrigger value='genel'>Genel</TabsTrigger>
              <TabsTrigger value='sokakLezzetleri'>Sokak Lezzetleri</TabsTrigger>
              <TabsTrigger value='dogaMangal'>Doğa & Mangal</TabsTrigger>
              <TabsTrigger value='ozelOtobus'>Özel Otobüsler</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value='genel' className='space-y-4'>
            <div className='flex justify-end gap-4 mb-4'>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.bilecik.edu.tr/dosya/32071_fe0e_32068_025b_takvim....pdf"
              >
                <Button
                  variant="outline"
                  className="transition-all duration-300 hover:scale-105 hover:bg-primary hover:text-primary-foreground"
                >
                  Akademik Takvimi İndir
                </Button>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="#"
              >
                <Button
                  variant="outline"
                  className="transition-all duration-300 hover:scale-105 hover:bg-primary hover:text-primary-foreground"
                >
                  Sınav Takvimi İndir
                </Button>
              </a>
            </div>
            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Toplam Sınav Sayısı</CardTitle>
                </CardHeader>
                <CardContent>
                  {istatistiklerLoading ? (
                    <Skeleton className='h-8 w-16' />
                  ) : (
                    <div className='text-2xl font-bold'>{istatistikler.toplamSinav ?? 0}</div>
                  )}
                  <p className='text-xs text-muted-foreground'>Önceki dönem ve senenin soruları</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Dersler</CardTitle>
                </CardHeader>
                <CardContent>
                  {istatistiklerLoading ? (
                    <Skeleton className='h-8 w-16' />
                  ) : (
                    <div className='text-2xl font-bold'>{istatistikler.toplamDers ?? 0}</div>
                  )}
                  <p className='text-xs text-muted-foreground'>PDF'ler ve ders notları</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Eğitmenler</CardTitle>
                </CardHeader>
                <CardContent>
                  {istatistiklerLoading ? (
                    <Skeleton className='h-8 w-16' />
                  ) : (
                    <div className='text-2xl font-bold'>{istatistikler.toplamEgitmen ?? 0}</div>
                  )}
                  <p className='text-xs text-muted-foreground'>Yol gösterici liderler</p>
                </CardContent>
              </Card>
              <Card
                className="bg-gradient-to-br from-[#e50914]/10 to-[#e50914]/20 dark:from-[#e50914]/20 dark:to-[#e50914]/30 border-[#e50914]/30 dark:border-[#e50914]/50 hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer"
                onClick={() => window.open('https://zink.tr', '_blank', 'noopener,noreferrer')}
              >
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <img
                    src="https://zink-web.vercel.app/zink-logo.png"
                    alt="ZINK Logo"
                    className="h-6 w-auto"
                  />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold text-[#e50914] dark:text-[#e50914]'>ZINK</div>
                  <p className='text-xs text-muted-foreground'>Yerel teslimat uygulaması</p>
                </CardContent>
              </Card>
            </div>
            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-4'>
              <Button
                variant="outline"
                className="h-24 w-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 hover:scale-105 transition-all duration-200 border-blue-200 dark:border-blue-800"
                asChild
              >
                <a href="https://giris.bilecik.edu.tr/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center space-y-2">
                  <GraduationCap className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium">OBS Giriş</span>
                </a>
              </Button>

              <Button
                variant="outline"
                className="h-24 w-full bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 hover:scale-105 transition-all duration-200 border-green-200 dark:border-green-800"
                asChild
              >
                <a
                  href={aylikMenu || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center space-y-2"
                >
                  <Utensils className="h-8 w-8 text-green-600 dark:text-green-400" />
                  <span className="font-medium">Aylık Menü</span>
                </a>
              </Button>

              <Button
                variant="outline"
                className="h-24 w-full bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 hover:scale-105 transition-all duration-200 border-purple-200 dark:border-purple-800"
                asChild
              >
                <a href="https://unka.bilecik.edu.tr:82/User/Login" className="flex flex-col items-center justify-center space-y-2">
                  <Wallet className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  <span className="font-medium">Para Yükleme</span>
                </a>
              </Button>

              <Button
                variant="outline"
                className="h-24 w-full bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 hover:scale-105 transition-all duration-200 border-orange-200 dark:border-orange-800"
                onClick={() => setShowDevelopers(true)}
              >
                <div className="flex flex-col items-center justify-center space-y-2">
                  <Code2 className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                  <span className="font-medium">Geliştiriciler</span>
                </div>
              </Button>

              <Dialog open={showDevelopers} onOpenChange={setShowDevelopers}>
                <DialogContent className="sm:max-w-[700px]"> {/* Genişliği artırdık */}
                  <DialogHeader className="text-center space-y-4">
                    <DialogTitle className="text-2xl font-bold">Geliştiriciler</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-8 py-4">
                    <div className="flex flex-col items-center space-y-4">
                      <img
                        src="https://cdn.wexbie.com/images/kullanici/pp/133114470.jpg"
                        alt="Geliştirici 1"
                        className="h-24 w-24 rounded-full object-cover border-2 border-primary"
                      />
                      <div className="text-center space-y-1">
                        <h3 className="font-bold text-lg">wexbie</h3>
                        <p className="text-sm text-muted-foreground">Frontend Developer</p>
                      </div>
                      <div className="flex space-x-4">
                        <a href="https://github.com/wexbie" target="_blank" rel="noopener noreferrer"
                          className="hover:text-primary transition-colors">
                          <Github className="h-5 w-5" />
                        </a>
                        <a href="https://linkedin.com/in/wexbie" target="_blank" rel="noopener noreferrer"
                          className="hover:text-primary transition-colors">
                          <Linkedin className="h-5 w-5" />
                        </a>
                        <a href="mailto:eyupaslnn00@email.com"
                          className="hover:text-primary transition-colors">
                          <Mail className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                    <div className="flex flex-col items-center space-y-4">
                      <img
                        src="https://cdn.wexbie.com/images/kullanici/pp/81803760.jpg"
                        alt="xrenata"
                        className="h-24 w-24 rounded-full object-cover border-2 border-primary"
                      />
                      <div className="text-center space-y-1">
                        <h3 className="font-bold text-lg">xrenata</h3>
                        <p className="text-sm text-muted-foreground">Backend Developer</p>
                      </div>
                      <div className="flex space-x-4">
                        <a href="https://github.com/xrenata" target="_blank" rel="noopener noreferrer"
                          className="hover:text-primary transition-colors">
                          <Github className="h-5 w-5" />
                        </a>
                        <a href="https://linkedin.com/in/xrenata" target="_blank" rel="noopener noreferrer"
                          className="hover:text-primary transition-colors">
                          <Linkedin className="h-5 w-5" />
                        </a>
                        <a href="mailto:xrenata@email.com"
                          className="hover:text-primary transition-colors">
                          <Mail className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-7'>
              <Card className="col-span-1 lg:col-span-4 border shadow-md rounded-lg">
                <CardHeader>
                  <CardTitle>Duyurular</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      ))}
                    </div>
                  ) : error ? (
                    <div className="text-center py-4 text-red-500">{error}</div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tarih</TableHead>
                            <TableHead>Başlık</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getCurrentPageAnnouncements().map((announcement) => (
                            <TableRow key={announcement.id}>
                              <TableCell>{announcement.date}</TableCell>
                              <TableCell>
                                {announcement.link ? (
                                  <a
                                    href={announcement.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    {announcement.title}
                                  </a>
                                ) : (
                                  announcement.title
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="mt-4">
                        <Pagination>
                          <PaginationContent>
                            {currentPage > 1 && (
                              <PaginationItem>
                                <PaginationPrevious onClick={() => setCurrentPage(currentPage - 1)} />
                              </PaginationItem>
                            )}
                            {Array.from({ length: Math.ceil(announcements.length / itemsPerPage) }).map((_, i) => (
                              <PaginationItem key={i}>
                                <PaginationLink
                                  onClick={() => setCurrentPage(i + 1)}
                                  isActive={currentPage === i + 1}
                                >
                                  {i + 1}
                                </PaginationLink>
                              </PaginationItem>
                            ))}
                            {currentPage < Math.ceil(announcements.length / itemsPerPage) && (
                              <PaginationItem>
                                <PaginationNext onClick={() => setCurrentPage(currentPage + 1)} />
                              </PaginationItem>
                            )}
                          </PaginationContent>
                        </Pagination>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card className='col-span-1 lg:col-span-3'>
                <CardHeader>
                  <CardTitle>MYO Bölümler</CardTitle>
                </CardHeader>
                <CardContent>
                  {bolumlerLoading ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            <Skeleton className="h-4 w-24" />
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...Array(6)].map((_, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Skeleton className="h-4 w-48" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bölümler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bolumler.map((bolum) => (
                          <TableRow key={bolum._id}>
                            <TableCell>
                              <Link
                                to="/bolumler/bolumDetay/$bolumId"
                                params={{ bolumId: bolum._id }}
                                className="hover:underline text-primary"
                              >
                                {bolum.ad}
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value='sokakLezzetleri' className="space-y-6">
            {restoranlarLoading ? (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="shadow-md rounded-2xl overflow-hidden">
                    <Skeleton className="w-full h-48 rounded-none" />
                    <div className="p-5 space-y-4">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {restoranlar.map((restoran) => (
                  <Card
                    key={restoran._id}
                    className="rounded-2xl overflow-hidden"
                  >
                    <img
                      src={restoran.kapakFoto}
                      alt={restoran.isim}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <h3 className="text-xl font-bold mb-2">{restoran.isim}</h3>
                      <p className="mb-4">{restoran.konum}</p>
                      <a
                        href={restoran.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 w-full text-blue-600 font-medium transition-colors duration-300"
                      >
                        <Button variant="outline" className="w-full flex items-center justify-center space-x-2 text-primary">
                          <span>Konuma Git</span>
                          <MapPin className="h-4 w-4 text-primary" />
                        </Button>
                      </a>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value='dogaMangal' className="space-y-6">
            {aktivitelerLoading ? (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="shadow-md rounded-2xl overflow-hidden">
                    <Skeleton className="w-full h-48 rounded-none" />
                    <div className="p-5 space-y-4">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {aktiviteler.map((aktivite) => (
                  <Card
                    key={aktivite._id}
                    className="rounded-2xl overflow-hidden"
                  >
                    <img
                      src={aktivite.kapakFoto}
                      alt={aktivite.isim}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <h3 className="text-xl font-bold mb-2">{aktivite.isim}</h3>
                      <p className="mb-4">{aktivite.konum}</p>
                      <a
                        href={aktivite.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 w-full text-blue-600 font-medium transition-colors duration-300"
                      >
                        <Button variant="outline" className="w-full flex items-center justify-center space-x-2 text-primary">
                          <span>Konuma Git</span>
                          <MapPin className="h-4 w-4 text-primary" />
                        </Button>
                      </a>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value='ozelOtobus' className="space-y-4">
            {otobusSaatleriLoading ? (
              <div className="grid gap-4 md:grid-cols-2">
                {[...Array(2)].map((_, i) => (
                  <Card key={i}>
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-6 w-48" />
                      <Skeleton className="h-4 w-32" />
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-lg border">
                        <div className="space-y-2 p-4">
                          <div className="flex justify-between">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-4 w-24" />
                          </div>
                          {[...Array(7)].map((_, j) => (
                            <div key={j} className="flex justify-between border-b pb-2">
                              <Skeleton className="h-4 w-16" />
                              <Skeleton className="h-4 w-16" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {otobusSaatleri.map((otobus) => {
                  const rotaAdi = otobus.rota.includes('Bilecik') ? 'Pazaryeri - Bilecik Merkez' : 'Pazaryeri - Bozüyük';
                  const hedefAdi = otobus.rota.includes('Bilecik') ? 'Bilecik\'ten' : 'Bozüyük\'ten';

                  return (
                    <Card key={otobus._id}>
                      <CardHeader>
                        {otobus.not && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {otobus.not}
                          </p>
                        )}
                        <CardTitle>{rotaAdi}</CardTitle>
                        <CardDescription>Günlük otobüs sefer saatleri</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-lg border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Pazaryeri'nden</TableHead>
                                <TableHead>{hedefAdi}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {otobus.saatler.map((saat, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium">{saat.pazaryerinden}</TableCell>
                                  <TableCell>{saat.hedefe}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Main >
    </>
  );
}

const topNav = [
  {
    title: "Ana Sayfa",
    href: "/",
    isActive: true,
  },
  {
    title: "Bölümler",
    href: "/bolumler",
    isActive: false,
  },
  {
    title: "Eğitmenler",
    href: "/egitmenler",
    isActive: false,
  },
];
